import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowUpRight, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { changePassword } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

/**
 * Modal for rotating the admin password from inside the inbox.
 * Renders nothing when `open` is false.
 */
export const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState("");
  const [nextPw, setNextPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setCurrent("");
    setNextPw("");
    setConfirmPw("");
    setError("");
    setSuccess(false);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (nextPw.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (nextPw !== confirmPw) {
      setError("New password and confirmation do not match.");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, nextPw);
      setSuccess(true);
      toast.success("Password updated.");
      setTimeout(() => handleClose(), 1600);
    } catch (err) {
      const msg = formatDetail(err?.response?.data?.detail) || err?.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-[rgba(20,17,13,0.45)] backdrop-blur-sm flex items-start md:items-center justify-center px-4 py-16 overflow-y-auto"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative bg-[var(--bg)] border border-[var(--line-strong)] w-full max-w-lg p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              data-testid={ADMIN.changePwClose}
              className="absolute top-4 right-4 text-[var(--ink-70)] hover:text-[var(--ink)] p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="overline mb-4">Admin · Change password</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.05] text-[var(--ink)] mb-2">
              Rotate the{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                keys
              </span>
              .
            </h2>
            <p className="text-[var(--ink-70)] text-sm mb-8">
              Pick a strong password — at least 8 characters. You&apos;ll stay
              signed in on this device.
            </p>

            <form onSubmit={onSubmit} className="space-y-2">
              <div>
                <label className="overline block mb-2">Current password</label>
                <input
                  required
                  type="password"
                  autoComplete="current-password"
                  className="editorial-input"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  data-testid={ADMIN.changePwCurrent}
                />
              </div>
              <div className="pt-4">
                <label className="overline block mb-2">New password</label>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  className="editorial-input"
                  value={nextPw}
                  onChange={(e) => setNextPw(e.target.value)}
                  data-testid={ADMIN.changePwNew}
                />
              </div>
              <div className="pt-4">
                <label className="overline block mb-2">Confirm new password</label>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  className="editorial-input"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  data-testid={ADMIN.changePwConfirm}
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm pt-4" data-testid={ADMIN.changePwError}>
                  {error}
                </div>
              )}
              {success && (
                <div
                  className="flex items-center gap-2 text-[var(--accent)] pt-4"
                  data-testid={ADMIN.changePwSuccess}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm uppercase tracking-[0.22em]">
                    Password updated
                  </span>
                </div>
              )}

              <div className="pt-8 flex items-center gap-4">
                <button
                  type="submit"
                  disabled={loading || success}
                  className="btn-pill disabled:opacity-60"
                  data-testid={ADMIN.changePwSubmit}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Updating…</span>
                    </>
                  ) : (
                    <>
                      <span>Update password</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-sm uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--ink)] link-slide"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChangePasswordModal;
